/**
 * Sidebar Component
 * Optimized version using signals and externalized assets
 */

import { Component, inject, computed } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { SidebarService } from '../services/sidebar.service';
import { HeaderFacadeService } from '../services/header-facade.service';
import { AuthService } from '../../services/auth.service';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import { MatRippleModule } from '@angular/material/core';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { NotificationsDrawerComponent } from '../notifications-drawer/notifications-drawer.component';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatMenuModule,
    MatDividerModule,
    MatButtonModule,
    MatTooltipModule,
    MatBadgeModule,
    MatRippleModule,
    MatListModule,
    MatIconModule,
    NotificationsDrawerComponent
  ],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css']
})
export class SidebarComponent {
  protected readonly sidebarService = inject(SidebarService);
  protected readonly headerFacade = inject(HeaderFacadeService);
  protected readonly document = inject(DOCUMENT);

  protected readonly auth = inject(AuthService);
  protected readonly router = inject(Router);

  // Computes user full name from backend profile or falls back to headerFacade default
  protected readonly userName = computed(() => {
    const profile = this.auth.currentUserProfile();
    if (!profile) {
      return this.headerFacade.userName();
    }
    
    // Resolve Name
    const nameWords = profile.adminName.trim().split(/\s+/);
    let resolvedName = nameWords[0] || '';
    if (nameWords.length > 1) {
      const firstName = nameWords[0];
      const secondName = nameWords[1];
      resolvedName = firstName.length < 5 ? secondName : firstName;
    }

    // Resolve Last Name
    const lastNameWords = profile.adminLastName.trim().split(/\s+/);
    let resolvedLastName = lastNameWords[0] || '';
    if (lastNameWords.length > 1) {
      const firstLastName = lastNameWords[0];
      const secondLastName = lastNameWords[1];
      resolvedLastName = firstLastName.length < 5 ? secondLastName : firstLastName;
    }

    return `${resolvedName} ${resolvedLastName}`.trim();
  });

  // Computes user initials from backend profile or falls back to headerFacade default
  protected readonly userInitial = computed(() => {
    const name = this.userName();
    return name ? name.charAt(0).toUpperCase() : this.headerFacade.userInitial();
  });

  // Computes sport center name from backend profile
  protected readonly sportCenterName = computed(() => {
    const profile = this.auth.currentUserProfile();
    return profile ? profile.sportCenterName : 'Centro deportivo';
  });

  // Determines if the authenticated user is the main manager of the sport center
  protected readonly isMainManager = computed(() => {
    const profile = this.auth.currentUserProfile();
    return profile ? profile.mainManager : false;
  });

  // Computes notification count from backend profile or falls back to headerFacade default
  protected readonly notificationCount = computed(() => {
    const profile = this.auth.currentUserProfile();
    return profile ? profile.notificationCount : this.headerFacade.unreadCount();
  });

  openNotifications = false;
  openUserMenu = false;
  sidebarCollapsed = false;

  toggleNotifications(): void {
    this.openNotifications = !this.openNotifications;
    if (this.openNotifications) {
      this.openUserMenu = false;
      // Hide sidebar only on mobile/small screens (width < 768px) where both cannot fit
      const isSmallScreen = window.innerWidth < 768;
      if (isSmallScreen && this.sidebarService.isOpen()) {
        this.sidebarService.toggleSidebar();
        this.sidebarCollapsed = true;
        this.document.body.classList.add('sidebar-collapsed');
      }
    } else {
      this.openUserMenu = false;
      // Keep collapsed state on mobile/small screens when closing notifications
      const isSmallScreen = window.innerWidth < 768;
      if (isSmallScreen) {
        this.sidebarCollapsed = true;
        this.document.body.classList.add('sidebar-collapsed');
      }
    }
  }

  toggleUserMenu(): void {
    this.openUserMenu = !this.openUserMenu;
    if (this.openUserMenu) {
      this.openNotifications = false;
    }
  }

  toggleCollapse(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
    if (this.sidebarCollapsed) {
      this.document.body.classList.add('sidebar-collapsed');
      this.openNotifications = false;
      this.openUserMenu = false;
    } else {
      this.document.body.classList.remove('sidebar-collapsed');
      // Also reopen the sidebar service when showing the menu
      if (!this.sidebarService.isOpen()) {
        this.sidebarService.toggleSidebar();
      }
    }
  }

  onLogout(): void {
    this.auth.clear();
    this.router.navigate(['/login']);
  }

  onMenuItemClick(): void {
    const isSmallScreen = window.innerWidth < 768;
    if (isSmallScreen && this.sidebarService.isOpen()) {
      this.sidebarService.toggleSidebar();
    }
  }
}
